import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import ImportPanel from "@/components/admin/ImportPanel";
import ProductsPanel from "@/components/admin/ProductsPanel";
import CustomersPanel from "@/components/admin/CustomersPanel";
import SettingsPanel from "@/components/admin/SettingsPanel";
import ActivityPanel from "@/components/admin/ActivityPanel";
import FilesPanel from "@/components/admin/FilesPanel";
import StaffPanel from "@/components/admin/StaffPanel";
import PreviewPanel from "@/components/admin/PreviewPanel";
import { Button } from "@/components/ui/button";
import { Upload, Boxes, Users, Settings, LogOut, AlertTriangle, Activity, FolderOpen, UserCog, Eye } from "lucide-react";

const has = (user, perm) => !!user && (user.is_admin || (user.permissions || []).includes(perm));

const ALL_TABS = [
  { id: "import", label: "Price Import", icon: Upload, show: (u) => has(u, "catalog") },
  { id: "products", label: "Products & Mapping", icon: Boxes, show: (u) => has(u, "catalog") || has(u, "marketing") },
  { id: "preview", label: "Price List Preview", icon: Eye, show: (u) => has(u, "catalog") || has(u, "marketing") || has(u, "quoting") },
  { id: "files", label: "Files", icon: FolderOpen, show: (u) => has(u, "marketing") },
  { id: "customers", label: "Customers", icon: Users, show: (u) => has(u, "quoting") },
  { id: "activity", label: "Activity", icon: Activity, show: (u) => has(u, "quoting") },
  { id: "staff", label: "Staff Accounts", icon: UserCog, show: (u) => !!u && u.is_admin },
  { id: "settings", label: "Settings", icon: Settings, show: () => true },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const tabs = ALL_TABS.filter((t) => t.show(user));
  const [tab, setTab] = useState(null);
  const [counts, setCounts] = useState({ total: 0, unclassified: 0, orphaned: 0 });

  useEffect(() => {
    if ((tab === null || !tabs.some((t) => t.id === tab)) && tabs.length) setTab(tabs[0].id);
  }, [tabs, tab]);

  const canProducts = has(user, "catalog") || has(user, "marketing");
  const refreshCounts = () => { if (canProducts) api.get("/admin/products/counts").then((r) => setCounts(r.data)).catch(() => {}); };
  useEffect(() => { refreshCounts(); }, [tab]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-slate-900 text-white flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-slate-800 flex items-center gap-3">
          <img src="/thermaco-logo.webp" alt="Thermaco" className="h-8 bg-white rounded-sm px-1.5 py-1" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Pricing Portal</div>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            const badge = t.id === "products" && counts.unclassified > 0 ? counts.unclassified : null;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`nav-${t.id}`}
                className={`w-full flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors ${
                  active ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1 text-left">{t.label}</span>
                {badge && (
                  <span className="bg-amber-500 text-slate-900 text-xs font-bold rounded-sm px-1.5 py-0.5">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        {canProducts && counts.orphaned > 0 && (
          <div className="mx-4 mb-4 flex items-start gap-2 text-xs text-amber-300 bg-amber-950/40 border border-amber-800/50 rounded-sm p-3">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{counts.orphaned} orphaned SKU{counts.orphaned > 1 ? "s" : ""} — review in Products.</span>
          </div>
        )}
        <div className="border-t border-slate-800 p-4">
          <div className="text-xs text-slate-400 mb-2 truncate" data-testid="admin-email">{user?.email}</div>
          <Button
            variant="outline" size="sm" onClick={logout} data-testid="logout-button"
            className="w-full rounded-sm bg-transparent border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 p-8 max-w-[1400px]">
        {tab === "import" && <ImportPanel onDone={refreshCounts} goProducts={() => setTab("products")} />}
        {tab === "products" && <ProductsPanel onChange={refreshCounts} />}
        {tab === "preview" && <PreviewPanel />}
        {tab === "files" && <FilesPanel />}
        {tab === "customers" && <CustomersPanel />}
        {tab === "activity" && <ActivityPanel />}
        {tab === "staff" && <StaffPanel />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}
